import messages from './messages'
import {
  CHAT_ANSWER_TRUNCATED,
  CHAT_SERVICE_UNAVAILABLE,
  CHAT_TIMEOUT,
  CONVERSATION_NOT_FOUND,
  FORBIDDEN,
  INTERNAL_ERROR,
  INVALID_INPUT,
  MISSING_COMPANY_CONTEXT,
  MISSING_CUSTOMER_CONTEXT,
  NETWORK_ERROR,
  UNAUTHORIZED,
} from './constants'

export const getChatScopeCode = ({ customerCode, companyCode }) =>
  companyCode || customerCode || null

export const getChatBasePath = ({ customerCode, companyCode }) => {
  if (customerCode && companyCode) {
    return `/${customerCode}/${companyCode}`
  }

  if (customerCode) {
    return `/${customerCode}`
  }

  return '/'
}

export const getChatPagePath = (params) => `${getChatBasePath(params)}/chat`

const splitPathAndSearch = (pathWithSearch = '') => {
  const [path, ...searchParts] = pathWithSearch.split('?')

  return {
    path: path || '/',
    search: searchParts.length > 0 ? `?${searchParts.join('?')}` : '',
  }
}

export const getChatRouteKind = ({ pathname, customerCode, companyCode }) => {
  const segments = pathname.split('/').filter(Boolean)
  let currentIndex = 0

  if (customerCode && segments[currentIndex] === customerCode) {
    currentIndex += 1
  }

  if (companyCode && segments[currentIndex] === companyCode) {
    currentIndex += 1
  }

  const currentSection = segments[currentIndex]

  if (!currentSection) {
    if (companyCode) return 'company-home'
    if (customerCode) return 'customer-home'
    return 'root'
  }

  return currentSection
}

export const buildChatContext = ({
  location,
  customerCode,
  companyCode,
  chatSurface,
  documentTitle,
}) => {
  const currentPath = location?.pathname || '/'
  const currentSearch = location?.search || ''
  const returnTo = location?.state?.returnTo
  const { path: contextPath, search: contextSearch } = returnTo
    ? splitPathAndSearch(returnTo)
    : { path: currentPath, search: currentSearch }

  return {
    chatSurface,
    currentPath,
    currentSearch,
    contextPath,
    contextSearch,
    routeKind: getChatRouteKind({
      pathname: contextPath,
      customerCode,
      companyCode,
    }),
    pageTitle: documentTitle || null,
    customerCode: customerCode || null,
    companyCode: companyCode || null,
  }
}

export const isCompanyScopedConversation = (conversation) => {
  const companyIds = conversation?.companyIds

  return Array.isArray(companyIds) && companyIds.length > 0
}

export const filterConversationsForContext = (
  conversations,
  { companyCode }
) => {
  if (companyCode) {
    return conversations
  }

  return conversations.filter(
    (conversation) => !isCompanyScopedConversation(conversation)
  )
}

/**
 * The localized message for a chat error code.
 *
 * The error object reaching the store carries the backend's own `Content` string in `message`, which
 * is written in English. Rendering that as a fallback put English sentences such as "Conversation not
 * found" into the Finnish UI, and leaked implementation-oriented HTTP text along with them. Every code
 * the backend can emit is mapped here instead, and anything unrecognised gets the localized generic
 * message rather than the raw text.
 *
 * TOKEN_LIMIT_EXCEEDED is deliberately absent: it renders usage and reset-date detail of its own, so
 * both views special-case it before calling this.
 */
export const CHAT_ERROR_MESSAGE_KEY_BY_CODE = {
  [UNAUTHORIZED]: 'chatUnauthorized',
  [FORBIDDEN]: 'chatForbidden',
  [CHAT_SERVICE_UNAVAILABLE]: 'chatServiceUnavailable',
  [CHAT_TIMEOUT]: 'chatTimeout',
  [CHAT_ANSWER_TRUNCATED]: 'chatAnswerTruncated',
  [INVALID_INPUT]: 'chatInvalidInput',
  [CONVERSATION_NOT_FOUND]: 'chatConversationNotFound',
  [MISSING_CUSTOMER_CONTEXT]: 'chatMissingContext',
  [MISSING_COMPANY_CONTEXT]: 'chatMissingContext',
  [NETWORK_ERROR]: 'chatNetworkError',
  [INTERNAL_ERROR]: 'errorSending',
}

export const resolveChatErrorMessage = (intl, error) => {
  const key = CHAT_ERROR_MESSAGE_KEY_BY_CODE[(error?.code)]

  return intl.formatMessage(messages[key] || messages.errorSending)
}
